import { gql, useQuery } from '@apollo/client';
import loadImage from 'image-promise';
// eslint-disable-next-line no-restricted-imports
import { ResizeObserver } from '@juggle/resize-observer';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from '../utils/router/v3Bridge';
import ContentPreview from '../components/ContentPreview';
import LoadingIndicator from '../components/LoadingIndicator';
import type {
  ExternalSearchPreview_PreviewFragment,
  ExternalSearchPreviewQuery,
  ExternalSearchPreviewQueryVariables,
} from '../graphql/generated/graphqlTypes';
import {
  SEARCH_PLATFORM_INFO_MAP,
  SearchPreviewMode,
} from '../integrations/IntegrationPlatformInfo';
import { stripHighlight } from '../utils/search/renderHighlight';
import styles from './ExternalSearchPreview.module.scss';

const PREVIEW_FRAGMENT = gql`
  fragment ExternalSearchPreview_preview on MentionPreview {
    content
    imageUrl
  }
`;

const EXTERNAL_SEARCH_PREVIEW_QUERY = gql`
  query externalSearchPreview($externalId: String!, $platform: String!) {
    externalSearchPreview(externalId: $externalId, platform: $platform) {
      imageUrl
      content
    }
  }
`;

interface ExternalSearchPreviewProps {
  externalId: string;
  icon: React.ReactNode;
  platform: string;
  preview?: ExternalSearchPreview_PreviewFragment | null;
  title: string;
  url: string;
  handleSetPreviewSettings?: (ratio: number, maxWidth: number) => void;
}

const ExternalSearchPreview = ({
  externalId,
  icon,
  platform,
  preview: localPreview,
  title,
  url,
  handleSetPreviewSettings,
}: ExternalSearchPreviewProps) => {
  const { searchPreview } = SEARCH_PLATFORM_INFO_MAP[platform] || {};
  const { data: { externalSearchPreview: remotePreview } = {}, loading } =
    useQuery<ExternalSearchPreviewQuery, ExternalSearchPreviewQueryVariables>(
      EXTERNAL_SEARCH_PREVIEW_QUERY,
      {
        variables: { externalId, platform },
        skip: !!localPreview || searchPreview !== SearchPreviewMode.Single,
      },
    );

  const [hasFailed, setHasFailed] = useState(false);
  const preview = localPreview || remotePreview;

  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

  const hasPreview =
    !hasFailed && preview && (preview.imageUrl || preview.content);
  const imageRef = useRef<HTMLImageElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (previewRef.current && isPreviewLoaded) {
      const resizeObserver = new ResizeObserver(() => {
        const rect = previewRef.current?.getBoundingClientRect();
        const widthToHeightRatio = (rect?.height ?? 1) / (rect?.width ?? 1);
        handleSetPreviewSettings?.(
          widthToHeightRatio,
          imageRef.current?.naturalWidth ?? 300,
        );
        resizeObserver.disconnect();
      });
      resizeObserver.observe(previewRef.current);

      return () => resizeObserver.disconnect();
    }

    return undefined;
  }, [handleSetPreviewSettings, isPreviewLoaded]);

  useEffect(() => {
    if (hasPreview && preview.imageUrl && imageRef.current) {
      loadImage(imageRef.current)
        .then(() => {
          setIsPreviewLoaded(true);
        })
        .catch(() => setHasFailed(true));
    }
  }, [hasPreview, preview]);

  return (
    <Link to={url} target="_blank" className={styles.container}>
      <div ref={previewRef}>
        {hasPreview ? (
          <>
            {preview.imageUrl ? (
              <div className={styles.imageContainer}>
                <img
                  ref={imageRef}
                  src={preview.imageUrl}
                  alt={stripHighlight(title)}
                />
              </div>
            ) : (
              <div className={styles.contentContainer}>
                <ContentPreview content={preview.content} numLines={Infinity} />
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyContainer}>
            {loading ? (
              <LoadingIndicator delay={0} />
            ) : (
              <div>No preview available</div>
            )}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.icon}>{icon}</div>
        <div className={styles.title}>{stripHighlight(title)}</div>
      </div>
    </Link>
  );
};

ExternalSearchPreview.fragments = {
  preview: PREVIEW_FRAGMENT,
};

export default ExternalSearchPreview;
